import React, { useCallback, useEffect, useRef, useState } from 'react';
import ClientSmsThreadPanel from '../../../customer-messaging/components/ClientSmsThreadPanel';
import './AppointmentSmsChatPopout.css';

const MOBILE_MQ = '(max-width: 768px)';

const MessageIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * Floating chat for appointment details — FAB on desktop, bottom sheet on mobile.
 */
const AppointmentSmsChatPopout = ({
  companyId,
  customerPhone,
  clientDisplayName,
  clientAvatarUrl
}) => {
  const [open, setOpen] = useState(false);
  const fabRef = useRef(null);
  const closeBtnRef = useRef(null);
  const panelRef = useRef(null);
  const prevFocusRef = useRef(null);

  const title = clientDisplayName?.trim() || 'Client';

  const handleOpen = useCallback(() => {
    prevFocusRef.current = document.activeElement;
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const isMobileViewport = useCallback(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches,
    []
  );

  const syncMobileViewport = useCallback(() => {
    const panel = panelRef.current;
    if (!panel || !isMobileViewport()) return;

    const vv = window.visualViewport;
    if (!vv) {
      panel.style.removeProperty('--appt-chat-vv-height');
      panel.style.removeProperty('--appt-chat-keyboard-offset');
      return;
    }

    const keyboardOffset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    panel.style.setProperty('--appt-chat-vv-height', `${Math.round(vv.height)}px`);
    panel.style.setProperty('--appt-chat-keyboard-offset', `${Math.round(keyboardOffset)}px`);
  }, [isMobileViewport]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  useEffect(() => {
    if (!open || !isMobileViewport()) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open, isMobileViewport]);

  useEffect(() => {
    if (!open || !isMobileViewport()) return undefined;

    syncMobileViewport();
    const vv = window.visualViewport;
    if (!vv) return undefined;

    vv.addEventListener('resize', syncMobileViewport);
    vv.addEventListener('scroll', syncMobileViewport);
    window.addEventListener('orientationchange', syncMobileViewport);

    return () => {
      vv.removeEventListener('resize', syncMobileViewport);
      vv.removeEventListener('scroll', syncMobileViewport);
      window.removeEventListener('orientationchange', syncMobileViewport);
      panelRef.current?.style.removeProperty('--appt-chat-vv-height');
      panelRef.current?.style.removeProperty('--appt-chat-keyboard-offset');
    };
  }, [open, isMobileViewport, syncMobileViewport]);

  useEffect(() => {
    if (open) {
      closeBtnRef.current?.focus();
      return undefined;
    }

    const prev = prevFocusRef.current;
    if (prev && typeof prev.focus === 'function') {
      prev.focus();
    } else {
      fabRef.current?.focus();
    }
    return undefined;
  }, [open]);

  if (!companyId) return null;

  return (
    <div className="appt-sms-popout" data-open={open ? 'true' : 'false'}>
      {open && (
        <>
          <button
            type="button"
            className="appt-sms-popout__backdrop"
            onClick={handleClose}
            aria-label="Close chat"
          />
          <div
            id="appt-sms-popout-panel"
            ref={panelRef}
            className="appt-sms-popout__panel"
            role="dialog"
            aria-modal="true"
            aria-label={`Chat with ${title}`}
          >
            <div className="appt-sms-popout__handle" aria-hidden />
            <header className="appt-sms-popout__header">
              <div className="appt-sms-popout__header-text">
                <h2 className="appt-sms-popout__title">{title}</h2>
                <p className="appt-sms-popout__subtitle">Text conversation</p>
              </div>
              <button
                ref={closeBtnRef}
                type="button"
                className="appt-sms-popout__close"
                onClick={handleClose}
                aria-label="Close chat"
              >
                <CloseIcon />
              </button>
            </header>
            <div className="appt-sms-popout__body">
              <ClientSmsThreadPanel
                companyId={companyId}
                customerPhone={customerPhone}
                clientDisplayName={clientDisplayName}
                clientAvatarUrl={clientAvatarUrl}
                variant="compact"
                showHeader={false}
                className="appt-sms-popout__thread"
              />
            </div>
          </div>
        </>
      )}

      {!open && (
        <button
          ref={fabRef}
          type="button"
          className="appt-sms-popout__fab"
          onClick={handleOpen}
          aria-expanded={false}
          aria-controls="appt-sms-popout-panel"
          aria-label="Open chat with client"
        >
          <MessageIcon />
          <span className="appt-sms-popout__fab-label">Chat</span>
        </button>
      )}
    </div>
  );
};

export default AppointmentSmsChatPopout;
